interface QrFinderEyeLogoProps {
  size?: number
  className?: string
  animating?: boolean
}

// Ring geometry (all in viewBox units):
//   Stroke path: rounded square (0.5, 0.5)→(6.5, 6.5), corner radius 1.0
//   Stroke width: 1  →  outer edge r=1.5, inner edge r=0.5 (matches filled ring)
//   Perimeter: 4 straight segments × 4 + 4 quarter-circles × π/2 = 16 + 2π ≈ 22.2832
//   Gap (bridge): 1 unit wide.  Solid: 21.2832.
//
// stroke-dashoffset=21.2832 places the gap at path position 0..1,
// which corresponds to x=1.5..2.5 on the top edge — matching the
// static bridge location exactly.  Animating from 21.2832 → −1
// moves the gap clockwise one full revolution.

const PERIMETER = 16 + 2 * Math.PI  // ≈ 22.2832
const GAP       = 1
const SOLID     = PERIMETER - GAP    // ≈ 21.2832
// Initial dashoffset that places the gap at the bridge position
const INITIAL_OFFSET = SOLID

/**
 * A single QR finder-eye corner rendered as an SVG icon.
 *
 * Geometry matches the 3-D plaque/stencil exports:
 *   - 7×7 module outer square with 1.5 module corner radius
 *   - 2.25×2.25 module rounded centre dot (0.75 module corner radius)
 *   - 1 module-thick ring with a bridge gap
 *
 * The ring is drawn as a thick stroke on the centreline so that
 * stroke-dashoffset can animate the gap along the path.  The gap
 * always cuts perpendicular to the ring edge (no angling), and the
 * initial offset places it exactly where the static bridge sits
 * (no jump on start).
 */
export default function QrFinderEyeLogo({
  size = 40,
  className = '',
  animating = false,
}: QrFinderEyeLogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-1 -1 9 9"
      fill="currentColor"
      className={className}
      style={{ display: 'block' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {animating && (
        <style>{`
          @keyframes qr-bridge {
            from { stroke-dashoffset: ${INITIAL_OFFSET}; }
            to   { stroke-dashoffset: ${INITIAL_OFFSET - PERIMETER}; }
          }
        `}</style>
      )}

      {/*
        Ring as a thick stroke.  stroke-dasharray creates the bridge gap;
        stroke-dashoffset positions/animates it.
      */}
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="butt"
        strokeDasharray={`${SOLID} ${GAP}`}
        style={{
          strokeDashoffset: INITIAL_OFFSET,
          ...(animating ? { animation: 'qr-bridge 3s linear infinite' } : {}),
        }}
        d="M1.5 0.5L5.5 0.5A1 1 0 0 1 6.5 1.5L6.5 5.5A1 1 0 0 1 5.5 6.5L1.5 6.5A1 1 0 0 1 0.5 5.5L0.5 1.5A1 1 0 0 1 1.5 0.5Z"
      />

      {/* Invisible hit area covering the full viewBox so cursor is consistent */}
      <rect x="-1" y="-1" width="9" height="9" fill="transparent" />

      {/* Centre dot */}
      <path d="M3.875 2.375A0.75 0.75 0 0 1 4.625 3.125L4.625 3.875A0.75 0.75 0 0 1 3.875 4.625L3.125 4.625A0.75 0.75 0 0 1 2.375 3.875L2.375 3.125A0.75 0.75 0 0 1 3.125 2.375Z" />
    </svg>
  )
}
